import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import _ from "lodash";
import { useLazyQuery } from "@apollo/client";
import useReduxState from "Hooks/useReduxState";
import { useI18n } from "Hooks";
import {
  getDefaultStudentNameOrderPreference,
  getOrganizationId,
} from "modules/CommonSelectors";
import { getCalendarEventItemUsersBatch } from "../modules/ParticipantsQueries";
import {
  SEARCH_DEBOUNCE_MS,
  RANGE_BATCH_DELAY_MS,
} from "../modules/ParticipantsConstants";
import { TAB_TYPES } from "../context/ParticipantsModalContext";
import { normaliseParticipant } from "../utils/normaliseParticipant";

const TYPE_STUDENT = "Student";
const TYPE_STAFF = "Staff";
const TYPE_FAMILY = "FamilyMember";

/**
 * Given an entity from associatedEntities, resolve its concrete type name.
 * Prefers the `type` field, falls back to `__typename`.
 */
const resolveTypeName = entity => {
  const typeName = entity?.type || entity?.__typename;
  const lower = _.toLower(typeName);
  if (lower === "student") return TYPE_STUDENT;
  if (lower === "staff") return TYPE_STAFF;
  if (_.includes(["familymember", "family_member", "parent"], lower)) {
    return TYPE_FAMILY;
  }
  return typeName;
};

/**
 * Manages batch-fetching of user details for a virtualised participant list
 * where associatedEntities carry only minimal data (id + type).
 *
 * Uses a single `calendarEventItemUsers` query on the Organization node with
 * `includedUserIds` to scope results to the known participant set. Handles
 * Student, Staff, and FamilyMember in one call — no type grouping required.
 *
 * Batch mode: as the user scrolls, `onRangeChanged` is called with the
 * visible index range. The hook collects visible IDs not yet in `detailsMap`,
 * debounces them, then fires a single batch query.
 *
 * Search mode: when `searchText` is non-empty, all un-loaded IDs are fetched
 * so client-side name filtering covers the full participant list.
 *
 * @param {{
 *   associatedEntities: Array<{ id: string, type?: string, __typename?: string }>,
 *   activeTab: string,
 *   searchText: string,
 *   skip: boolean,
 * }} params
 * @returns {{
 *   onRangeChanged: ({ startIndex: number, endIndex: number }) => void,
 *   detailsMap: Record<string, object>,
 *   filteredActiveTabIds: string[],
 *   totalCount: { students: number, staff: number, family: number },
 *   isLoading: boolean,
 * }}
 */
const useUserBatchDetails = ({
  associatedEntities,
  activeTab,
  searchText,
  skip = false,
  eventType,
}) => {
  const { t } = useI18n(["common"]);
  const defaultStudentNameOrderPreference = useReduxState(
    getDefaultStudentNameOrderPreference
  );
  const organizationId = getOrganizationId();

  // -------------------------------------------------------------------------
  // Group IDs by concrete type (for totalCount tab badges only)
  // -------------------------------------------------------------------------
  const { studentIds, staffIds, familyIds } = useMemo(() => {
    const students = [];
    const staff = [];
    const family = [];
    _.forEach(associatedEntities, entity => {
      if (!entity?.id) return;
      const typeName = resolveTypeName(entity);
      if (typeName === TYPE_STUDENT) students.push(entity.id);
      else if (typeName === TYPE_STAFF) staff.push(entity.id);
      else if (typeName === TYPE_FAMILY) family.push(entity.id);
    });
    return { studentIds: students, staffIds: staff, familyIds: family };
  }, [associatedEntities]);

  // -------------------------------------------------------------------------
  // State & refs
  // -------------------------------------------------------------------------
  const [detailsMap, setDetailsMap] = useState({});
  const detailsMapRef = useRef({});
  const inFlightIds = useRef(new Set());
  const pendingIdsRef = useRef(new Set());
  const batchTimerRef = useRef(null);
  const generationRef = useRef(0);

  // Search debounce
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Stable identity for entity list
  const entitiesKey = useMemo(() => _.map(associatedEntities, "id").join(","), [
    associatedEntities,
  ]);

  // Reset on entity list change
  const prevEntitiesKey = useRef(entitiesKey);
  useEffect(() => {
    if (prevEntitiesKey.current !== entitiesKey) {
      prevEntitiesKey.current = entitiesKey;
      generationRef.current += 1;
      detailsMapRef.current = {};
      setDetailsMap({});
      inFlightIds.current = new Set();
      pendingIdsRef.current = new Set();
      if (batchTimerRef.current) {
        clearTimeout(batchTimerRef.current);
        batchTimerRef.current = null;
      }
      setDebouncedSearchText("");
    }
  }, [entitiesKey]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (batchTimerRef.current) {
        clearTimeout(batchTimerRef.current);
      }
    };
  }, []);

  // -------------------------------------------------------------------------
  // Single lazy query — calendarEventItemUsers on Organization node
  // -------------------------------------------------------------------------
  const [fetchUsersBatch] = useLazyQuery(getCalendarEventItemUsersBatch, {
    fetchPolicy: "cache-and-network",
  });

  /**
   * Merge calendarEventItemUsers edges into detailsMap, normalising each item
   * via normaliseParticipant.
   */
  const mergeBatchResults = useCallback(
    (data, fetchedForIds) => {
      const edges = _.get(data, "node.calendarEventItemUsers.edges", []);

      setDetailsMap(prev => {
        const next = { ...prev };
        _.forEach(edges, edge => {
          const item = edge?.node;
          if (item && item.id) {
            next[item.id] = normaliseParticipant(
              item,
              defaultStudentNameOrderPreference,
              t
            );
          }
        });
        detailsMapRef.current = next;
        return next;
      });

      setIsSearchFetching(false);

      // Clear in-flight tracking for every ID we requested (some may not have
      // been returned by the server if they are inactive / deleted).
      _.forEach(fetchedForIds, id => inFlightIds.current.delete(id));
    },
    [defaultStudentNameOrderPreference, t]
  );

  /**
   * Fire a single calendarEventItemUsers batch for the given IDs.
   * Stored in a ref so the search effect and scroll handler always call the
   * latest version without needing it as a useEffect dependency (which would
   * cause re-fires whenever mergeBatchResults recreates).
   */
  const fetchBatchRef = useRef(null);
  fetchBatchRef.current = (ids, extraFilters = {}) => {
    if (_.isEmpty(ids) || !organizationId) return;
    const dispatchGen = generationRef.current;

    fetchUsersBatch({
      variables: {
        organizationId,
        filters: {
          includedUserIds: ids,
          userStatus: "ACTIVE",
          ...(eventType ? { eventType } : {}),
          ...extraFilters,
        },
      },
    })
      .then(({ data }) => {
        if (dispatchGen !== generationRef.current) return;
        mergeBatchResults(data, ids);
      })
      .catch(() => {
        if (dispatchGen !== generationRef.current) return;
        _.forEach(ids, id => inFlightIds.current.delete(id));
        setIsSearchFetching(false);
      });
  };

  // Keep latest debouncedSearchText accessible inside the flush callback
  // without making flushPendingBatch depend on it (which would restart the
  // debounce timer on every keystroke).
  const debouncedSearchTextRef = useRef(debouncedSearchText);
  debouncedSearchTextRef.current = debouncedSearchText;

  // Flush accumulated pending IDs into a batch request.
  const flushPendingBatch = useCallback(() => {
    const ids = Array.from(pendingIdsRef.current);
    pendingIdsRef.current = new Set();
    batchTimerRef.current = null;
    if (_.isEmpty(ids)) return;
    const currentSearch = debouncedSearchTextRef.current;
    fetchBatchRef.current(
      ids,
      currentSearch ? { searchText: currentSearch } : {}
    );
  }, []);

  // -------------------------------------------------------------------------
  // Active tab IDs
  // -------------------------------------------------------------------------
  const activeTabIds = useMemo(() => {
    if (activeTab === TAB_TYPES.STUDENTS) return studentIds;
    if (activeTab === TAB_TYPES.STAFF) return staffIds;
    if (activeTab === TAB_TYPES.FAMILY) return familyIds;
    return [];
  }, [activeTab, studentIds, staffIds, familyIds]);

  // Ref for filteredActiveTabIds so onRangeChanged stays stable across
  // search-text changes without losing track of the current visible IDs.
  const filteredIdsRef = useRef([]);

  // Track whether a search fetch is in-flight so we can show skeletons instead
  // of an empty list while waiting for results.
  const [isSearchFetching, setIsSearchFetching] = useState(false);

  // -------------------------------------------------------------------------
  // Visible IDs for the active tab
  // -------------------------------------------------------------------------
  // While a search fetch is in-flight, show full activeTabIds so Virtuoso
  // renders skeletons. Once results arrive (isSearchFetching false), filter
  // down to only IDs the server returned (i.e. those present in detailsMap).
  const filteredActiveTabIds = useMemo(() => {
    const trimmed = _.trim(searchText);
    if (_.isEmpty(trimmed)) return [...activeTabIds];
    if (isSearchFetching) return [...activeTabIds];
    return _.filter(activeTabIds, id => Boolean(detailsMap[id]));
  }, [activeTabIds, searchText, detailsMap, isSearchFetching]);

  filteredIdsRef.current = filteredActiveTabIds;

  // -------------------------------------------------------------------------
  // Virtuoso range handler
  // -------------------------------------------------------------------------
  const onRangeChanged = useCallback(
    ({ startIndex, endIndex }) => {
      if (skip) return;

      const visibleIds = filteredIdsRef.current.slice(startIndex, endIndex + 1);
      const toFetch = _.filter(
        visibleIds,
        id => !detailsMapRef.current[id] && !inFlightIds.current.has(id)
      );
      if (_.isEmpty(toFetch)) return;

      _.forEach(toFetch, id => {
        inFlightIds.current.add(id);
        pendingIdsRef.current.add(id);
      });

      if (batchTimerRef.current) {
        clearTimeout(batchTimerRef.current);
      }
      batchTimerRef.current = setTimeout(
        flushPendingBatch,
        RANGE_BATCH_DELAY_MS
      );
    },
    [flushPendingBatch, skip]
  );

  // -------------------------------------------------------------------------
  // Fetch on tab change or search change.
  //
  // When search is active: reset detailsMap and fire all activeTabIds with
  // searchText — server filters by name within that set.
  //
  // When search is empty (tab switch, or search cleared): reset detailsMap
  // and immediately fetch the first page of IDs for the new tab so Virtuoso
  // doesn't need to fire onRangeChanged to trigger the first load.
  // -------------------------------------------------------------------------
  const INITIAL_BATCH_SIZE = 30;

  useEffect(() => {
    if (skip || _.isEmpty(activeTabIds)) return;

    // Reset so stale results from the previous tab/search don't remain.
    generationRef.current += 1;
    detailsMapRef.current = {};
    setDetailsMap({});
    inFlightIds.current = new Set();
    pendingIdsRef.current = new Set();
    if (batchTimerRef.current) {
      clearTimeout(batchTimerRef.current);
      batchTimerRef.current = null;
    }

    if (debouncedSearchText) {
      // Search mode: pass all IDs of the current tab with searchText.
      setIsSearchFetching(true);
      _.forEach(activeTabIds, id => inFlightIds.current.add(id));
      fetchBatchRef.current(activeTabIds, { searchText: debouncedSearchText });
    } else {
      // No search: eagerly fetch the first visible batch so the list isn't
      // blank waiting for Virtuoso to fire onRangeChanged.
      const initialBatch = activeTabIds.slice(0, INITIAL_BATCH_SIZE);
      _.forEach(initialBatch, id => inFlightIds.current.add(id));
      fetchBatchRef.current(initialBatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, debouncedSearchText, activeTabIds]);

  return {
    onRangeChanged,
    detailsMap,
    filteredActiveTabIds,
    totalCount: {
      students: studentIds.length,
      staff: staffIds.length,
      family: familyIds.length,
    },
    isLoading: false,
  };
};

export default useUserBatchDetails;
